import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { client } from '../../utils/contentfulClient';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';
import { Helmet } from 'react-helmet-async';
import proj from './Project/Project.module.css';

const BRAND_COLOR = '#2c3e50';

function SingleProject() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [newerProj, setNewerProj] = useState(null);
  const [olderProj, setOlderProj] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lightbox State
  const [lightboxData, setLightboxData] = useState({ isOpen: false, images: [], currentIndex: 0 });

  useEffect(() => {
    setLoading(true);
    // Fetch the project and include depth: 2 to ensure we get the nested Image Group data!
    client.getEntries({ content_type: 'portfolioProject', 'fields.slug': slug, include: 2 })
    .then((response) => {
      const current = response.items[0];
      setProject(current);

      if (current) {
        const createdAt = current.sys.createdAt;
        const fetchNewer = client.getEntries({ content_type: 'portfolioProject', 'sys.createdAt[gt]': createdAt, order: 'sys.createdAt', limit: 1, 'fields.targetSite': 'Fans & Feathers' });
        const fetchOlder = client.getEntries({ content_type: 'portfolioProject', 'sys.createdAt[lt]': createdAt, order: '-sys.createdAt', limit: 1, 'fields.targetSite': 'Fans & Feathers' });

        Promise.all([fetchNewer, fetchOlder]).then(([newerRes, olderRes]) => {
          setNewerProj(newerRes.items[0] || null);
          setOlderProj(olderRes.items[0] || null);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }).catch(console.error);
  }, [slug]);

  // Lightbox Navigation Controls (UPDATED to stop at boundaries)
  const closeLightbox = () => setLightboxData({ ...lightboxData, isOpen: false });
  
  const nextImage = (e) => { 
    e.stopPropagation(); 
    setLightboxData(prev => {
      // Check if we are already at the last image
      if (prev.currentIndex >= prev.images.length - 1) return prev;
      return { ...prev, currentIndex: prev.currentIndex + 1 };
    }); 
  };
  
  const prevImage = (e) => { 
    e.stopPropagation(); 
    setLightboxData(prev => {
      // Check if we are already at the first image
      if (prev.currentIndex <= 0) return prev;
      return { ...prev, currentIndex: prev.currentIndex - 1 };
    }); 
  };

  if (loading) return <p style={{ textAlign: 'center', marginTop: '50px' }}>Loading project...</p>;
  if (!project) return <p>Project not found!</p>;

  // NOTE: If app crashes here, check if fields in Contentful are "Text" vs "Rich Text" as discussed
  const { title, projectType, objectDimensions, coverImage, introduction, mainDescription, videoUrl, imageSet } = project.fields;

  // Intercept Asset Hyperlinks to open the Lightbox
  const richTextOptions = {
    renderNode: {
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        return (
          <span 
            onClick={(e) => {
              e.preventDefault();
              // The linked asset data is stored in node.data.target
              // We wrap it in an array so it perfectly matches the format your Lightbox expects!
              setLightboxData({
                isOpen: true,
                images: [node.data.target],
                currentIndex: 0
              });
            }}
            style={{ 
              color: BRAND_COLOR, 
              cursor: 'pointer', 
              textDecoration: 'underline',
              fontWeight: '500'
            }}
          >
            {children}
          </span>
        );
      }
    }
  };

  return (
    <article>
      <Helmet><title>{title} | Portfolio</title></Helmet>

      <div>                   
          <div className="container">

            <div style={{ marginTop: '30px' }}>              
              <Link to="/portfolio" style={{ textDecoration: 'none', color: BRAND_COLOR, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>
                &larr; Back to Portfolio
              </Link>
            </div>

              <div className="row">
                  <div className="col">
                      <div className={proj.name}>{title}</div>
                  </div>
              </div>
              <div className="row align-items-center row-cols-1 row-cols-md-2 ">
                  <div className="col">
                    {projectType && <span><div className={proj.subtitle}>Project Type</div><div className={proj.subtext}>{projectType}</div></span>}                    
                    {objectDimensions && <span><div className={proj.subtitle}>Dimensions</div><div className={proj.subtext}>{objectDimensions}</div></span>}
                  </div>
                  {introduction && (
                    <div className="col">                      
                      <div className={proj.subtext}>
                          {documentToReactComponents(introduction, richTextOptions)}
                      </div>
                  </div>
                  )}                 
              </div>
              <p></p>
              <div className="row">
                  <div className="col-md-12">
                      <div>
                        {coverImage && (
                          <figure>
                              <img className={proj.image} alt={title} src={`https:${coverImage.fields.file.url}`}></img>
                          </figure>                          
                        )}
                      </div>
                  </div>
                  <div className={proj.subtitle}>Conservation</div>
                  {mainDescription && (                    
                    <div className="elegant" style={{ width: '100%' }}>
                      {documentToReactComponents(mainDescription, richTextOptions)}
                    </div>
                  )}
              </div>                   

      {/* Image Set Rendering */}
      {imageSet && imageSet.length > 0 && (
        <div>
          <div className={proj.subtitle}>Project Gallery</div>                  
          <div className="row">
            {imageSet.map((group, groupIndex) => (
              
              <div key={groupIndex} className="col-md-6" style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {group.fields.images?.map((img, imgIndex) => (
                    <img 
                      key={imgIndex} 
                      src={`https:${img.fields.file.url}?w=400&h=300&fit=fill`} 
                      alt="Gallery thumbnail"
                      style={{ cursor: 'pointer', width: '45%', minWidth: '150px', objectFit: 'cover', borderRadius: '4px', transition: 'transform 0.2s' }}
                      onClick={() => setLightboxData({ isOpen: true, images: group.fields.images, currentIndex: imgIndex })}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ))}
                </div>
                {group.fields.description && (
                  <div style={{ textAlign: 'center', marginTop: '15px', color: '#666', fontStyle: 'italic', fontSize: '0.95rem' }}>
                    {group.fields.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}


       {/* Video Section (REVERTED TO IFRAME, HEIGHT 500) */}
      {videoUrl && (
        <div>
          <div className={proj.subtitle}>Project Video</div>          
          <iframe 
            width="100%" 
            height="500" 
            src={videoUrl.replace("watch?v=", "embed/")} 
            frameBorder="0" 
            allowFullScreen 
            title={`${title} Video`} 
            style={{ borderRadius: '8px' }}>
          </iframe>
        </div>
      )}
     

      {/* Lightbox Overlay */}
      {lightboxData.isOpen && (
        <div onClick={closeLightbox} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          
          <button onClick={closeLightbox} style={{ position: 'absolute', top: '20px', right: '30px', background: 'none', border: 'none', color: 'white', fontSize: '2rem', cursor: 'pointer' }}>
            &times;
          </button>
          
          {/* Prev Button (Centered Vertically) */}
          {lightboxData.images.length > 1 && lightboxData.currentIndex > 0 && (
             <button onClick={prevImage} style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'white', fontSize: '3rem', cursor: 'pointer', padding: '10px' }}>
               &lt;
             </button>
          )}
          
          {/* Main Image */}
          <img 
            src={`https:${lightboxData.images[lightboxData.currentIndex].fields.file.url}`} 
            alt={lightboxData.images[lightboxData.currentIndex].fields.title || "Gallery image"} 
            style={{ maxHeight: '75vh', maxWidth: '90vw', objectFit: 'contain' }} // Slightly reduced height to make room for description
            onClick={(e) => e.stopPropagation()} 
          />

          {/* NEW: Image Caption (Title & Description) */}
          <div 
            style={{ marginTop: '20px', textAlign: 'center', padding: '0 20px', color: '#fff', maxWidth: '700px' }}
            onClick={(e) => e.stopPropagation()} // Prevents closing the lightbox on click
          >
            {/* Display the Title if it exists */}
            {lightboxData.images[lightboxData.currentIndex].fields.title && (
              <div style={{ fontSize: '1.2rem', fontWeight: '400', letterSpacing: '1px' }}>
                {lightboxData.images[lightboxData.currentIndex].fields.title}
              </div>
            )}
            
            {/* NEW: Display the Description if it exists */}
            {lightboxData.images[lightboxData.currentIndex].fields.description && (
              <div style={{ color: '#ccc', marginTop: '8px', fontSize: '0.95rem', fontWeight: '300', fontStyle: 'italic', lineHeight: '1.4' }}>
                {lightboxData.images[lightboxData.currentIndex].fields.description}
              </div>
            )}
          </div>

          {/* Next Button (Centered Vertically) */}
          {lightboxData.images.length > 1 && lightboxData.currentIndex < lightboxData.images.length - 1 && (
            <button onClick={nextImage} style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'white', fontSize: '3rem', cursor: 'pointer', padding: '10px' }}>
              &gt;
            </button>
          )}
        </div>
      )}


      {/* Next/Prev Project Links */}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '30px' }}>
        <div style={{ flex: 1, textAlign: 'left' }}>
          {newerProj && (
            <Link to={`/portfolio/${newerProj.fields.slug}`} style={{ textDecoration: 'none', color: BRAND_COLOR }}>
              <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase' }}>&larr; Previous Project</span>
              <div style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{newerProj.fields.title}</div>
            </Link>
          )}
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          {olderProj && (
            <Link to={`/portfolio/${olderProj.fields.slug}`} style={{ textDecoration: 'none', color: BRAND_COLOR }}>
              <span style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase' }}>Next Project &rarr;</span>
              <div style={{ fontWeight: '400', marginTop: '8px', fontSize: '1.1rem' }}>{olderProj.fields.title}</div>
            </Link>
          )}
        </div>
      </div>
              </div>
      </div>
    </article>
  );
}

export default SingleProject;